import classNames from 'classnames';
import {
  type CSSProperties,
  forwardRef,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tree } from 'react-arborist';

import {
  type DrillMode,
  Node,
  type PopoverComponentProps,
  type TNode,
} from './Node';
import styles from './TreeSelection.module.css';
import { type TreeApi, type TreeApiProps, useTreeApi } from './useTreeApi';
import {
  searchMatch as defaultSearchMatch,
  sortSelectedFirst,
  sortTreeBySearchRelevance,
} from './utils';

export type TreeSelectionComponentProps = {
  treeApi: TreeApi;
  noSearchResults?: ReactNode;
  drillMode?: DrillMode;
  sortSuggestedSearch?: boolean;
  skipDefaultSort?: boolean;
  sortSelectedIds?: string[];
  flattenIndentation?: boolean;
  disabled?: boolean | number[];
  width?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLDivElement>;
  isLoading?: boolean;
  searchMatch?: (node: TNode, search: string) => boolean;
  showNodeCount?: boolean;
  popoverComponent?: React.ForwardRefExoticComponent<
    PopoverComponentProps & React.RefAttributes<HTMLDivElement>
  >;
};

/** Slightly longer than the tick draw in CheckboxIndicator.module.css. */
const ANIMATE_CHECK_MS = 250;

type TreeSelectionApiRequiredProps = Pick<
  TreeApiProps,
  'data' | 'openByDefault'
> &
  TreeSelectionComponentProps;

const TreeSelectionApiRequired = ({
  data,
  treeApi,
  openByDefault = false,
  onClick,
  drillMode,
  sortSuggestedSearch = false,
  skipDefaultSort = false,
  sortSelectedIds,
  flattenIndentation = false,
  disabled,
  height = 220,
  noSearchResults = 'No Results',
  className,
  style,
  isLoading,
  searchMatch = defaultSearchMatch,
  showNodeCount = true,
  popoverComponent,
}: TreeSelectionApiRequiredProps) => {
  const {
    treeRef,
    selectedIds,
    indeterminateSelectedIds,
    toggleNodeSelection,
    search,
  } = treeApi;

  // react-arborist memoizes its row on `{index, style}`, so a selection change
  // only reaches the rows because the inline renderer below is a fresh
  // component type each render — every row remounts. A remounted checkbox has
  // no memory of having just been ticked, so the id is recorded here (this
  // component does not remount) and only that row is told to animate.
  const lastToggleRef = useRef<{ id: string; at: number } | null>(null);
  const handleToggleNode = (id: string) => {
    lastToggleRef.current = { id, at: Date.now() };
    toggleNodeSelection(id);
  };
  // Held for the length of the draw rather than cleared on the next render.
  // A popout re-renders several times per toggle (drill panels, the selection
  // display, the parent filter form) and each render remounts every row, so
  // clearing on the first one killed the animation before it was visible.
  // Expiring by time also stops a later unrelated render — typing in the
  // search box — from replaying it.
  const shouldAnimateNode = (id: string) => {
    const last = lastToggleRef.current;
    return last?.id === id && Date.now() - last.at < ANIMATE_CHECK_MS;
  };

  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const openingSelectionState = useRef({
    selectedIds,
    indeterminateIds: indeterminateSelectedIds,
  });

  const [noResults, setNoResults] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const tree = treeRef.current;

    const numVisibleNodes = tree?.visibleNodes.length || 0;

    setNoResults(search.length > 0 && numVisibleNodes === 0 && !isLoading);
  }, [search, treeRef, treeRef.current?.visibleNodes, isLoading]);

  const initialSelectionState = useRef<{
    selectedIds: string[];
    indeterminateIds: string[];
  }>({
    selectedIds: [...selectedIds],
    indeterminateIds: [...indeterminateSelectedIds],
  });

  useEffect(() => {
    initialSelectionState.current = {
      selectedIds: [...selectedIds],
      indeterminateIds: [...indeterminateSelectedIds],
    };
  }, [selectedIds, indeterminateSelectedIds]);

  const suggestedSortedData = useMemo(() => {
    if (sortSuggestedSearch && search.length > 0) {
      return sortTreeBySearchRelevance([...data], search);
    }

    if (skipDefaultSort) {
      return [...data];
    }

    return sortSelectedFirst(
      data,
      sortSelectedIds ?? openingSelectionState.current.selectedIds,
      openingSelectionState.current.indeterminateIds
    );
  }, [data, search, sortSuggestedSearch, skipDefaultSort, sortSelectedIds]);

  return (
    <div
      ref={setContainerEl}
      className={classNames(styles.treeSelectionContainer, className)}
      style={style}
      onClick={onClick}
    >
      {noResults && (
        <div className={styles.noSearchText}>{noSearchResults}</div>
      )}

      {containerEl && (
        <Tree
          data={suggestedSortedData}
          className={styles.tree}
          rowClassName={classNames(
            styles.row,
            flattenIndentation && styles.noIndent
          )}
          {...((!drillMode || drillMode?.isRoot) && { ref: treeRef })}
          searchTerm={search}
          searchMatch={searchMatch}
          openByDefault={openByDefault}
          width="100%"
          height={height}
          rowHeight={32}
          disableDrag={true}
          disableDrop={true}
          dndRootElement={containerEl}
        >
          {(props) => {
            const isDisabled = (() => {
              if (typeof disabled === 'boolean') return disabled;
              if (Array.isArray(disabled)) {
                return disabled.includes(props.node.level);
              }
              return props.node.data.disabled;
            })();

            const isParentDisabled = (() => {
              if (Array.isArray(disabled) && props.node.parent) {
                return disabled.includes(props.node.parent.level);
              }
              return false;
            })();

            const rowStyle = flattenIndentation
              ? { ...props.style, paddingLeft: 4 }
              : props.style;

            return (
              <Node
                {...props}
                style={rowStyle}
                selected={selectedIds.includes(props.node.id)}
                halfCheck={indeterminateSelectedIds.includes(props.node.id)}
                disabled={isDisabled}
                isParentDisabled={isParentDisabled}
                onToggle={handleToggleNode}
                drillMode={drillMode}
                hideExpandIcon={flattenIndentation}
                PopoverComponent={popoverComponent}
                showNodeCount={showNodeCount}
                animateCheck={shouldAnimateNode(props.node.id)}
              />
            );
          }}
        </Tree>
      )}
    </div>
  );
};

/** TreeSelection Internal
 * treeApi is not provided
 * This is used when the TreeSelection component defines its own treeApi.
 * The treeApi can still be exposed to its parent via a ref */
type TreeSelectionInternalApiProps = TreeApiProps &
  Omit<TreeSelectionComponentProps, 'treeApi'> & { treeApi?: undefined };
const TreeSelectionInternalApi = forwardRef(
  (props: TreeSelectionInternalApiProps, ref: Ref<TreeApi | undefined>) => {
    const treeApi = useTreeApi(props);

    useImperativeHandle(ref, () => treeApi);

    return <TreeSelectionApiRequired {...props} treeApi={treeApi} />;
  }
);

/** TreeSelection External
 * treeApi provided
 * This is used when the table api should be controlled externally, and when
 * multiple trees are used in the same component, but share a single treeApi
 * (ex. the PopoutTree) */
type TreeSelectionExternalApiProps = TreeApiProps & TreeSelectionComponentProps;
const TreeSelectionExternalApi = (props: TreeSelectionExternalApiProps) => {
  return <TreeSelectionApiRequired {...props} />;
};

/** Exported TreeSelection
 * takes in props for both internal and external treeApi */
export type TreeSelectionProps =
  | TreeSelectionInternalApiProps
  | TreeSelectionExternalApiProps;
export const TreeSelection = forwardRef(
  (props: TreeSelectionProps, ref: Ref<TreeApi | undefined>) => {
    if (props.treeApi) {
      return <TreeSelectionExternalApi {...props} />;
    } else {
      return <TreeSelectionInternalApi {...props} ref={ref} />;
    }
  }
);
